/**
 * SigTDD Email Verification Page
 *
 * Handles email verification tokens from signup emails.
 * URL: /verify-email?token=...
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { takeLinkToken } from '../../lib/link-token';
import './AuthPages.css';

const CheckIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const LoadingSpinner = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="auth-spinner">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
  </svg>
);

interface VerifyEmailPageProps {
  onClose?: () => void;
  onLogin?: () => void;
}

export default function VerifyEmailPage({ onClose, onLogin }: VerifyEmailPageProps) {
  const { user, applyVerifiedUser, resendVerification } = useAuth();
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      // Token captured at module load (and removed from the URL); URL fallback for other loads
      const token = takeLinkToken() ?? new URLSearchParams(window.location.search).get('token');

      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided. Please check your email link.');
        return;
      }

      try {
        const res = await fetch('/api/auth/verify-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (res.ok) {
          if (data.user) applyVerifiedUser(data.user);
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(data.error || 'Verification failed. The link may have expired.');
        }
      } catch {
        setStatus('error');
        setErrorMessage('Network error. Please try again.');
      }
    };

    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/sigtdd.svg" alt="" className="auth-logo" />

          {status === 'loading' && (
            <>
              <div className="auth-status-icon auth-status-loading">
                <LoadingSpinner />
              </div>
              <h1>Verifying your email...</h1>
              <p>Please wait while we confirm your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="auth-status-icon auth-status-success">
                <CheckIcon />
              </div>
              <h1>Email verified</h1>
              <p>Your email address is confirmed and you are signed in.</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="auth-status-icon auth-status-error">
                <ErrorIcon />
              </div>
              <h1>Verification failed</h1>
              <p>{errorMessage}</p>
            </>
          )}
        </div>

        {status === 'success' && (
          <button className="auth-submit" onClick={onClose}>
            Open the app
          </button>
        )}

        {status === 'error' && (
          <div className="auth-actions">
            {user ? (
              <>
                <button
                  className="auth-submit"
                  onClick={async () => setResendNotice((await resendVerification()).message)}
                >
                  Send a new verification email
                </button>
                {resendNotice && (
                  <p className="auth-hint" style={{ marginTop: '1rem', textAlign: 'center' }}>{resendNotice}</p>
                )}
                <button className="auth-link-btn" style={{ marginTop: '0.75rem' }} onClick={onClose}>
                  Open the app
                </button>
              </>
            ) : (
              <>
                <button className="auth-submit" onClick={onLogin}>
                  Sign in to request a new link
                </button>
                <p className="auth-hint" style={{ marginTop: '1rem', textAlign: 'center' }}>
                  Once signed in, use Resend in the banner at the top of the app.
                </p>
              </>
            )}
          </div>
        )}

        {onClose && (
          <div className="auth-back">
            <button className="auth-back-link" onClick={onClose}>
              Back to App
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
